import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	ClickAwayListener,
	Unstable_TrapFocus as FocusTrap,
	Popper,
	type PopperPlacementType,
	type SxProps,
	type Theme,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { type ReactNode, type SyntheticEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { BADGE, FLOATING_MENU, FONT_SIZE, INK, MOTION, NEUTRAL, RADIUS, SHADOW, SURFACE } from "@/theme/tokens";
import { InHouseMobileSheet } from "@/ui/Popups/InHouseMobileSheet";
import { portalOverlayProps } from "@/ui/Popups/portalOverlay";
import { DropdownPanel } from "./DropdownPanel";
import { DOT_SIZE } from "./DropdownRow";
import { DropdownTrigger } from "./DropdownTrigger";
import { fitChips, fitNames, measureTextWidth } from "./fitNames";
import type { DropdownProps, DropdownSearchConfig, NormalizedItem } from "./types";
import { useDropdownKeyboard } from "./useDropdownKeyboard";
import { useDropdownSearch } from "./useDropdownSearch";
import useDropdownState from "./useDropdownState";
import { filterBySegment, filterCollapsedGroups, toOptionIds } from "./visibleOptions";

const MOBILE_QUERY = "(max-width:600px)";
const SINGLE_SEARCH_THRESHOLD_DEFAULT = 7;
const MIN_PANEL_WIDTH = 260;
/** Single-select panels size to content between the trigger width and this cap (FIX 2b). */
const SINGLE_PANEL_MAX_WIDTH = 420;
/** The open panel's edge (px), which a `menu` look's inset counts with its padding. */
const PANEL_BORDER_PX = 1;
/** Shared identity for "nothing is collapsed", so the memos below do not churn on a fresh empty Set each render. */
const NO_COLLAPSED_GROUPS: ReadonlySet<string> = new Set<string>();
/** Gap kept between the popover edge and the viewport edge when clamping the panel to available height. */
const VIEWPORT_MARGIN = 8;
/**
 * How far above MUI's modal layer the options open — the desktop popover and the phone sheet alike. The options are
 * a transient pick opened FROM something (a dialog, a sheet, a form under a phone's bottom bar), so they must cover
 * it: on the drawer layer the phone sheet opened BEHIND a dialog it was opened from, and under a bar pinned to the
 * bottom of the screen.
 */
const PANEL_LAYER_ABOVE_MODAL = 200;

const asSearchConfig = <T,>(search: boolean | DropdownSearchConfig<T> | undefined): { on: boolean | undefined; config: DropdownSearchConfig<T> } => {
	if (typeof search === "object") return { on: true, config: search };
	return { on: search, config: {} };
};

/** Default single-select display: color dot / icon + selectedLabel. */
const renderSingleDisplay = <T,>(item: NormalizedItem<T> | undefined): ReactNode => {
	if (!item) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: INK.SECONDARY, minWidth: 0 }}>
			{item.icon}
			{item.color && <Box sx={{ width: DOT_SIZE, height: DOT_SIZE, borderRadius: "50%", flex: "0 0 auto", backgroundColor: item.color }} />}
			<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
				{item.selectedLabel}
			</Box>
		</Box>
	);
};

export const SystemDropdown = <T,>({
	items,
	selectedItem,
	selectedItems,
	onChange,
	onChangeMultiple,
	label,
	fieldLabel,
	required,
	errorKey,
	formControlSx,
	selectSx,
	valueToString,
	maxMenuHeight = 320,
	menuWidth,
	menuPlacement,
	multiple = false,
	minimalSelection,
	maximalSelection,
	fullWidth,
	disabled,
	clearable,
	renderValue,
	multiAsOne,
	hideCheckbox,
	allOption,
	createNewOption,
	onOpen,
	onClose,
	"data-testid": dataTestId,
	ariaLabel,
	maxVisibleChips,
	placeholder,
	// Opt-in: force the trigger to render even with an empty selection so a custom `renderValue` can paint
	// an "all / no filter" state (e.g. a faceted filter showing "All facilities" when nothing is picked).
	// Defaults to `!!placeholder` — the historical behavior — so callers that never set it are unchanged.
	displayEmpty: displayEmptyProp,
	// v1 opt-in
	trigger = "field",
	hideTriggerCount,
	triggerIcon,
	// DEFAULT: a dropdown shows its caret; only a trigger in a row of caret-less toolbar buttons drops it.
	hideTriggerCaret = false,
	// DEFAULT: the field panel every dropdown had.
	panelLook = "standard",
	panelTitle,
	menuActions,
	// DEFAULT: the panel lines up with the trigger's start edge, as every dropdown did.
	menuAlign = "start",
	removableChips,
	showSelectedOnly,
	search,
	searchThreshold = SINGLE_SEARCH_THRESHOLD_DEFAULT,
	autoSelectSingleMatch = true,
	loading,
	panelNote,
	mobileSheet = true,
	commit = "live",
	stopPropagationOnKeyDown = true,
	// legacy: `showFilter={false}` explicitly disables search (see searchEnabled below); truthy/undefined stay no-op.
	showFilter,
	// accepted no-ops (backward-compat)
	hideChip: _hideChip, // trigger-display only; the new default already shows a summary (chips are opt-in via removableChips)
	showNewBadge: _showNewBadge,
	emptyAsAll: _emptyAsAll,
	// Legacy custom "All" label — still honored so callers that pass it (and not `allOption`) keep their label.
	renderAllValueText,
	MenuProps: _MenuProps,
	collapsibleGroups = false,
	groupSelectAll: _groupSelectAll,
	id,
}: DropdownProps<T> & { id?: string }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const triggerRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	// "All"/"Selected" segment is owned here (not inside DropdownPanel) so the keyboard hook below navigates
	// the exact rows the panel renders — otherwise arrow/Enter act on rows hidden by the "Selected" segment.
	const [segment, setSegment] = useState<"all" | "selected">("all");
	const [panelWidth, setPanelWidth] = useState<number>(MIN_PANEL_WIDTH);
	// Available inner width + font of the trigger's display, measured to fit the multi summary names (FIX 1).
	const [triggerMetrics, setTriggerMetrics] = useState<{ width: number; font: string } | null>(null);
	const isMobile = useMediaQuery(MOBILE_QUERY);
	const theme = useTheme();
	const panelZIndex = theme.zIndex.modal + PANEL_LAYER_ABOVE_MODAL;
	const useSheet = isMobile && mobileSheet;

	// Vertical space the popover may occupy, measured from the trigger to the viewport edges — so the panel
	// fits SHORT screens (small laptops, landscape phones, split panes) instead of always requesting a fixed
	// `maxMenuHeight` and overflowing its footer off-screen. `max(above, below)` mirrors Popper's flip: it
	// places on whichever side has room, and we've capped to that side's room. Re-measured on resize/scroll.
	const [viewportMax, setViewportMax] = useState(maxMenuHeight);
	// Placement is LOCKED to viewport geometry (not rendered content height). Popper's default flip modifier
	// re-picks the side every time the panel resizes, so searching — which shrinks the list — would flip a
	// panel that opened above back below (it now "fits"), yanking it across the trigger. We disable flip and
	// choose the side ourselves from the same above/below measurement that caps the height, so the panel only
	// ever grows/shrinks from its anchored edge.
	const [placement, setPlacement] = useState<PopperPlacementType>("bottom-start");
	// Prefer below; flip up only when the trigger sits low enough that above has more room (e.g. a field near a
	// dialog's bottom). Pairs with the `max(above, below)` height cap so the chosen side is the one we cap to.
	const readAnchorSpace = useCallback(() => {
		const anchor = triggerRef.current?.getBoundingClientRect();
		if (!anchor) return;
		const vh = window.visualViewport?.height ?? window.innerHeight;
		const below = vh - anchor.bottom - VIEWPORT_MARGIN;
		const above = anchor.top - VIEWPORT_MARGIN;
		// A caller can force the side (e.g. always-below inside a short dialog); otherwise auto-pick by room.
		const side = menuPlacement ?? (below >= above ? "bottom-start" : "top-start");
		setViewportMax(side === "top-start" ? above : below);
		setPlacement(menuAlign === "end" ? (side === "top-start" ? "top-end" : "bottom-end") : side);
	}, [menuPlacement, menuAlign]);
	useEffect(() => {
		if (!open || useSheet) return;
		const measure = readAnchorSpace;
		// Coalesce scroll/resize bursts to one layout read per animation frame: the capture-phase scroll
		// listener fires for every scroll anywhere in the document while open, and `measure` reflows — so an
		// unthrottled handler thrashes layout on scroll-heavy grid pages.
		let frame: number | null = null;
		const schedule = () => {
			if (frame !== null) return;
			frame = window.requestAnimationFrame(() => {
				frame = null;
				measure();
			});
		};
		measure(); // sync first measure so the first paint is correctly capped
		window.addEventListener("resize", schedule);
		window.addEventListener("scroll", schedule, true);
		const vv = window.visualViewport;
		vv?.addEventListener("resize", schedule);
		return () => {
			if (frame !== null) window.cancelAnimationFrame(frame);
			window.removeEventListener("resize", schedule);
			window.removeEventListener("scroll", schedule, true);
			vv?.removeEventListener("resize", schedule);
		};
	}, [open, useSheet, readAnchorSpace]);

	// Measure the trigger's text area (once + on resize) so the multi trigger can fit as many names as
	// physically render before spilling to "+N more".
	useLayoutEffect(() => {
		const root = triggerRef.current;
		if (!root) return;
		const measure = () => {
			const el = root.querySelector<HTMLElement>(".MuiSelect-select") ?? root;
			const cs = getComputedStyle(el);
			const padX = Number.parseFloat(cs.paddingLeft) + Number.parseFloat(cs.paddingRight);
			const width = Math.max(0, el.clientWidth - padX);
			const font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
			setTriggerMetrics((prev) => (prev && prev.width === width && prev.font === font ? prev : { width, font }));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(root);
		return () => observer.disconnect();
	}, []);

	// `maximalSelection` is a hard cap: a toggle that would exceed it is blocked in `useDropdownState`. Surface
	// WHY so the blocked click isn't a silent no-op, mirroring the min-selection snackbar (no-silent-errors).
	const notifyMaxExceeded = useCallback(() => {
		if (maximalSelection === undefined) return;
		showSnackbar(t("system.dropdown.maxSelection", { count: maximalSelection }), "error");
	}, [maximalSelection, showSnackbar, t]);

	const state = useDropdownState<T>({
		items,
		selectedItem,
		selectedItems,
		onChange,
		onChangeMultiple,
		valueToString,
		multiple,
		minimalSelection,
		maximalSelection,
		multiAsOne,
		commit,
		open,
		onMaxExceeded: notifyMaxExceeded,
	});

	// Surface a caller misconfiguration (two options resolving to the same selection key) through analytics
	// rather than the console, so it's observable instead of silently swallowed. Fires once per distinct key.
	useEffect(() => {
		if (state.duplicateSelectionKey) {
			trackError("system_dropdown_duplicate_selection_key", { data_testid: dataTestId, selection_key: state.duplicateSelectionKey });
		}
	}, [state.duplicateSelectionKey, trackError, dataTestId]);

	const { on: searchOn, config: searchConfig } = asSearchConfig<T>(search);
	// Precedence: an explicit `search` prop wins; else legacy `showFilter={false}` forces search off;
	// else the mode default (multi always on, single only past `searchThreshold`).
	const legacyForcedOff = search === undefined && showFilter === false;
	const searchEnabled = legacyForcedOff ? false : (searchOn ?? (multiple ? true : state.selectableItems.length > searchThreshold));

	const listboxId = useId();

	const { query, setQuery, filteredItems, getHighlightSegments } = useDropdownSearch<T>(state.normalizedItems, searchConfig, searchEnabled);

	// Which `groupValues` headers are folded shut (opt-in `collapsibleGroups`). Owned here rather than inside the
	// panel so the keyboard hook below walks exactly the rows the panel paints.
	const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<ReadonlySet<string>>(NO_COLLAPSED_GROUPS);
	const toggleGroupCollapsed = useCallback((groupKey: string) => {
		setCollapsedGroupKeys((prev) => {
			const next = new Set(prev);
			if (!next.delete(groupKey)) next.add(groupKey);
			return next;
		});
	}, []);
	// A search must never hide its own matches, so every group reads as expanded while the query is live. The
	// user's collapsed set is kept rather than cleared — clearing the query restores the sections they folded.
	const effectiveCollapsedGroupKeys = collapsibleGroups && !query ? collapsedGroupKeys : NO_COLLAPSED_GROUPS;

	// Soft `minimalSelection`: never blocks a click; if the user LEAVES below the min, revert to the last
	// valid selection and flag it. Returns true if a violation was handled.
	const enforceMinOnLeave = useCallback((): boolean => {
		if (!state.minViolated) return false;
		state.restoreLastValid();
		const count = minimalSelection ?? 0;
		const name = label ?? fieldLabel;
		const message = name ? t("system.dropdown.minSelectionLabeled", { label: name, count }) : t("system.dropdown.minSelectionGeneric", { count });
		// This fires from a close triggered by an outside click; opt THIS snackbar out of clickaway dismissal
		// so the same gesture's trailing `click` doesn't dismiss it the instant it appears (other snackbars
		// keep their normal tap-to-dismiss behavior).
		showSnackbar(message, "error", undefined, undefined, { disableClickawayClose: true });
		return true;
	}, [state, minimalSelection, label, fieldLabel, showSnackbar, t]);

	const closePanel = useCallback(
		(event?: SyntheticEvent) => {
			// Live mode commits on every toggle, so a below-min selection is already in the caller's state →
			// validate + restore here. (Apply mode discards the buffer on outside-close; Done handles its check.)
			if (commit === "live") enforceMinOnLeave();
			setOpen(false);
			setQuery("");
			// Clear per-open UI state so a reopen starts fresh (query is reset above; the All/Selected
			// segment must reset too, else a reopened panel can land on a stale — possibly empty — "Selected" view).
			setSegment("all");
			onClose?.(event);
			// Return focus to the trigger for keyboard users.
			const focusable = triggerRef.current?.querySelector<HTMLElement>('[role="combobox"], .MuiSelect-select');
			focusable?.focus();
		},
		[commit, enforceMinOnLeave, onClose, setQuery],
	);

	// Single-select commits + closes on pick; multi live stays open; multi apply buffers.
	const handleToggleId = useCallback(
		(rowId: string) => {
			state.toggleId(rowId);
			track("system_dropdown_selection_changed", { data_testid: dataTestId, multiple: !!multiple });
			if (!multiple) closePanel();
		},
		[state, track, dataTestId, multiple, closePanel],
	);

	// Multi-select only: while searching, if the filter narrows to exactly one selectable option, auto-check
	// it. Additive only — it never deselects, so the "search never changes what is checked" invariant holds.
	// Deliberately NOT run in single-select: there a toggle REPLACES the selection and closes the panel, so
	// auto-selecting mid-typing would commit + snap shut before the user picks — a real invariant breach.
	// The ref makes it fire once per (query→single-match) transition, not on every render.
	const autoSelectedRef = useRef<string | null>(null);
	useEffect(() => {
		// Skip under the "Selected only" view: there the lone `filteredItems` match may be an UNSELECTED row the
		// segment hides, so auto-checking it would toggle a row the user can't see. (An already-selected match is
		// a no-op anyway.) So auto-select only applies to the visible "all" list.
		const selectedOnlyView = showSelectedOnly && segment === "selected";
		if (!autoSelectSingleMatch || !multiple || !open || !searchEnabled || !query.trim() || selectedOnlyView) {
			autoSelectedRef.current = null;
			return;
		}
		const matches = filteredItems.filter((item) => !item.groupValues && !item.disabled);
		if (matches.length !== 1) {
			autoSelectedRef.current = null;
			return;
		}
		const only = matches[0];
		if (state.effectiveIds.has(only.id) || autoSelectedRef.current === only.id) return;
		autoSelectedRef.current = only.id;
		handleToggleId(only.id);
	}, [autoSelectSingleMatch, multiple, open, searchEnabled, query, filteredItems, state.effectiveIds, handleToggleId, showSelectedOnly, segment]);

	const openPanel = useCallback(
		(event?: SyntheticEvent) => {
			if (commit === "apply") state.resetBuffer();
			state.baselineValid(); // snapshot the incoming (assumed-valid) selection for soft-min restore
			// Store the RAW trigger width; the MIN clamp is applied only in the multi width branch, so a narrow
			// single-select trigger (e.g. "Week") yields a narrow fit-content panel (FIX 2b).
			setPanelWidth(triggerRef.current?.offsetWidth ?? MIN_PANEL_WIDTH);
			// Seed placement synchronously from the trigger's geometry so the first painted frame is already on
			// the correct side (the resize/scroll effect runs post-paint and would otherwise flash bottom→top).
			readAnchorSpace();
			setOpen(true);
			onOpen?.(event);
			track("system_dropdown_opened", { data_testid: dataTestId, multiple: !!multiple, item_count: items.length });
		},
		[commit, state, onOpen, track, dataTestId, multiple, items.length, readAnchorSpace],
	);

	const onToggleTrigger = useCallback(
		(event: SyntheticEvent) => {
			if (disabled) return;
			if (open) closePanel(event);
			else openPanel(event);
		},
		[disabled, open, openPanel, closePanel],
	);

	const handleDone = useCallback(() => {
		if (commit === "apply") {
			// Validate the buffer before committing; below-min → revert to last valid + flag, then close
			// (closePanel is a no-op for the min-check in apply mode, so no double snackbar).
			if (enforceMinOnLeave()) {
				closePanel();
				return;
			}
			state.applyBuffer(() => closePanel());
			return;
		}
		closePanel();
	}, [commit, state, enforceMinOnLeave, closePanel]);

	const handleClickAway = useCallback(
		(event: MouseEvent | TouchEvent) => {
			if (triggerRef.current?.contains(event.target as Node)) return;
			closePanel();
		},
		[closePanel],
	);

	// Same helpers the panel renders from, so the keyboard highlights/toggles only rows the panel shows + allows —
	// the collapse filter included, since a collapsed group's children aren't on screen to be arrowed onto.
	const visibleOptionIds = useMemo(
		() =>
			toOptionIds(
				filterCollapsedGroups(filterBySegment(filteredItems, showSelectedOnly, segment, state.effectiveIds), effectiveCollapsedGroupKeys, state.idOf),
			),
		[filteredItems, showSelectedOnly, segment, state.effectiveIds, effectiveCollapsedGroupKeys, state.idOf],
	);
	const keyboard = useDropdownKeyboard({
		visibleOptionIds,
		onToggle: handleToggleId,
		onClose: () => closePanel(),
		hasQuery: !!query,
		clearQuery: () => setQuery(""),
		stopPropagationOnKeyDown,
	});

	// ---- trigger display ----
	const allOptionSuppressed = multiple && allOption === "";
	const allOptionLabel = multiple ? (allOptionSuppressed ? null : (allOption ?? t("system.dropdown.selectAll"))) : null;
	// `allOption` (new) wins; else the legacy `renderAllValueText`; else the generic default.
	const summaryAllLabel = (allOption && allOption !== "" ? allOption : renderAllValueText) || t("system.dropdown.allItems");

	// Mirror committedIds' null-handling: `selectedItem={null}` means "nothing chosen" (idOf(null) would either
	// fabricate a phantom "null" id or crash a value-dereferencing `valueToString`), so it must yield [].
	const valueIds = multiple ? [...state.effectiveIds] : selectedItem != null ? [state.idOf(selectedItem)] : [];

	const placeholderNode = useMemo(
		() =>
			placeholder ? (
				<Box component="span" sx={{ display: "block", color: INK.MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{placeholder}
				</Box>
			) : null,
		[placeholder],
	);

	const defaultRenderDisplay = useCallback(
		(value: string | string[]): ReactNode => {
			if (!multiple) {
				const idStr = Array.isArray(value) ? value[0] : value;
				const single = state.normalizedItems.find((item) => item.id === idStr);
				if (!single && placeholderNode) return placeholderNode;
				return renderSingleDisplay(single);
			}
			const selectedCount = state.selectableIds.filter((sid) => state.effectiveIds.has(sid)).length;
			if (multiAsOne) {
				if (state.isAllSelected && allOptionLabel) return <Box sx={{ color: INK.SECONDARY }}>{allOptionLabel}</Box>;
				const one = state.normalizedItems.find((item) => state.effectiveIds.has(item.id));
				return renderSingleDisplay(one);
			}
			if (removableChips) {
				const chosen = state.normalizedItems.filter((item) => state.effectiveIds.has(item.id));
				// Fit as many chips as render within the trigger's caret-free width, then "+N" — mirrors the
				// non-chip summary. Without this the raw pills overflow under the caret and out of the field.
				const { visible: visCount, hidden } = fitChips(
					chosen.map((item) => item.selectedText),
					triggerMetrics?.width ?? 0,
					triggerMetrics?.font ?? "500 14px sans-serif",
					maxVisibleChips,
				);
				const visible = chosen.slice(0, visCount);
				return (
					// `overflow: hidden` + `nowrap` is the safety net: even if the fit estimate is off, nothing paints
					// under the caret or outside the field.
					<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0, maxWidth: "100%", overflow: "hidden", flexWrap: "nowrap" }}>
						{visible.map((item) => (
							<Box
								key={item.key}
								component="span"
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.25,
									paddingInlineStart: 0.75,
									paddingInlineEnd: 0.25,
									// Fixed compact height keeps the chips trigger at the standard field height (no vertical growth).
									height: BADGE.STATUS_CHIP_HEIGHT_SM,
									flex: "0 0 auto",
									borderRadius: 1,
									backgroundColor: NEUTRAL[100],
									border: `1px solid ${NEUTRAL[200]}`,
									fontSize: FONT_SIZE.CAPTION,
									lineHeight: 1,
									color: INK.SECONDARY,
									whiteSpace: "nowrap",
								}}
							>
								{item.selectedLabel}
								<Box
									component="button"
									type="button"
									aria-label={t("system.dropdown.removeItem", { item: item.selectedText })}
									// stopPropagation so removing a chip doesn't also open the panel (the chip lives in the trigger).
									onPointerDown={(event) => event.stopPropagation()}
									onClick={(event) => {
										event.stopPropagation();
										state.toggleId(item.id);
									}}
									sx={{
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										border: 0,
										p: 0,
										cursor: "pointer",
										background: "none",
										color: INK.MUTED,
										borderRadius: "50%",
										"@media (hover: hover) and (pointer: fine)": { "&:hover": { color: INK.PRIMARY } },
									}}
								>
									<CloseIcon sx={{ fontSize: 14 }} />
								</Box>
							</Box>
						))}
						{hidden > 0 && <Box component="span" sx={{ color: INK.MUTED, fontWeight: 600, marginInlineStart: 0.25 }}>{`+${hidden}`}</Box>}
					</Box>
				);
			}
			if (selectedCount === 0) return placeholderNode;
			if (state.isAllSelected) return `${summaryAllLabel} (${state.selectableIds.length})`;
			// Show the chosen names, as many as fit in the trigger, then "+N more" (FIX 1).
			const names = state.normalizedItems.filter((item) => !item.groupValues && state.effectiveIds.has(item.id)).map((item) => item.selectedText);
			const { visible, hidden } = fitNames(names, triggerMetrics?.width ?? 0, triggerMetrics?.font ?? "500 14px sans-serif", maxVisibleChips);
			return (
				<Box component="span" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: INK.SECONDARY }}>
					{visible.join(", ")}
					{hidden > 0 && <Box component="span" sx={{ color: INK.MUTED, fontWeight: 600 }}>{` +${hidden} more`}</Box>}
				</Box>
			);
		},
		[multiple, multiAsOne, removableChips, maxVisibleChips, placeholderNode, state, allOptionLabel, summaryAllLabel, triggerMetrics, t],
	);

	const renderDisplay = (renderValue as ((value: string | string[]) => ReactNode) | undefined) ?? defaultRenderDisplay;
	const hasSelection = valueIds.length > 0;

	const clearSelection = useCallback(() => {
		if (multiple) state.clearAll();
		else onChange?.(undefined);
		track("system_dropdown_cleared", { data_testid: dataTestId, source: "trigger" });
	}, [multiple, state, onChange, track, dataTestId]);

	// Escape is handled once, inside `useDropdownKeyboard` (clears the query, then closes) — no duplicate
	// document-level listener, so a single Escape fires `onClose` at most once (L7).

	// DEFAULT: the panel's own word for its search; a menu's reads as a plain label in its borderless row, no ellipsis.
	const searchPlaceholder = searchConfig.placeholder ?? t(panelLook === "menu" ? "system.dropdown.menuSearchPlaceholder" : "system.dropdown.searchPlaceholder");

	// Panel width: caller's `menuWidth` wins; multi keeps a comfortable min; single-select is sized to its
	// widest row label (measured, since a flex+ellipsis row can't drive `max-content`), clamped between the
	// trigger width and a sensible max — so it's as wide as the content needs, not a fixed wide box (FIX 2b).
	const isMenuLook = panelLook === "menu";
	const minPanelWidth = isMenuLook ? FLOATING_MENU.MIN_WIDTH_PX : MIN_PANEL_WIDTH;
	// The menu's surface pads and edges its list itself, so the list is that much narrower and shorter than the menu.
	const menuInset = isMenuLook ? 2 * (FLOATING_MENU.PADDING_PX + PANEL_BORDER_PX) : 0;
	const panelWidthSx = useMemo<SxProps<Theme>>(() => {
		if (menuWidth !== undefined) return { width: menuWidth, maxWidth: "92vw" };
		if (multiple) return { width: Math.max(panelWidth, minPanelWidth) - menuInset, maxWidth: "92vw" };
		// The single-select width measures every label via canvas; the panel only renders while open, so skip the
		// O(n) measure loop entirely when closed (callers passing inline `items` re-run this on every render).
		if (!open) return { width: panelWidth };
		const font = triggerMetrics?.font ?? "500 14px sans-serif";
		// Chrome around the label: row padding + trailing check + a leading dot/icon allowance.
		const rowChrome = 76;
		const widest = state.normalizedItems.reduce((max, item) => Math.max(max, measureTextWidth(item.labelText, font)), 0);
		const width = Math.min(SINGLE_PANEL_MAX_WIDTH, Math.max(panelWidth, Math.ceil(widest) + rowChrome, isMenuLook ? minPanelWidth : 0));
		return { width: width - menuInset };
	}, [menuWidth, multiple, open, panelWidth, triggerMetrics, state.normalizedItems, minPanelWidth, isMenuLook, menuInset]);

	const panel = (
		<DropdownPanel<T>
			multiple={multiple}
			hideCheckbox={!!hideCheckbox}
			listboxId={listboxId}
			normalizedItems={state.normalizedItems}
			filteredItems={filteredItems}
			effectiveIds={state.effectiveIds}
			selectableIds={state.selectableIds}
			idOf={state.idOf}
			onToggleId={handleToggleId}
			onToggleVisible={(ids) => {
				state.toggleVisible(ids);
				track("system_dropdown_select_all_toggled", { data_testid: dataTestId });
			}}
			onToggleGroup={state.toggleGroup}
			collapsibleGroups={collapsibleGroups}
			collapsedGroupKeys={effectiveCollapsedGroupKeys}
			onToggleGroupCollapsed={toggleGroupCollapsed}
			onClearAll={() => {
				state.clearAll();
				track("system_dropdown_cleared", { data_testid: dataTestId });
			}}
			searchEnabled={searchEnabled}
			loading={loading}
			panelNote={panelNote}
			query={query}
			onQueryChange={setQuery}
			searchPlaceholder={searchPlaceholder}
			getHighlightSegments={getHighlightSegments}
			highlightedId={keyboard.highlightedId}
			allOptionLabel={allOptionLabel}
			showSelectedOnly={!!showSelectedOnly}
			segment={segment}
			onSegmentChange={setSegment}
			commitApply={commit === "apply"}
			onDone={handleDone}
			createNewOption={createNewOption}
			maxMenuHeight={maxMenuHeight}
			availableHeight={useSheet ? undefined : viewportMax - (isMenuLook ? menuInset + FLOATING_MENU.GAP_PX : 0)}
			widthSx={panelWidthSx}
			isSheet={useSheet}
			look={panelLook}
			panelTitle={panelTitle}
			menuActions={menuActions}
			closeForCommand={closePanel}
			dataTestId={dataTestId}
		/>
	);

	return (
		<>
			<DropdownTrigger
				ref={triggerRef}
				id={id}
				variant={trigger}
				disabled={disabled}
				open={open}
				controlsId={listboxId}
				onToggle={onToggleTrigger}
				label={label}
				required={required}
				errorText={errorKey ? t(errorKey) : undefined}
				fullWidth={fullWidth}
				multiple={multiple}
				valueIds={valueIds}
				renderDisplay={renderDisplay}
				hasSelection={hasSelection}
				displayEmpty={displayEmptyProp ?? !!placeholder}
				clearable={clearable}
				onClear={clearSelection}
				count={multiple && !hideTriggerCount ? state.selectableIds.filter((sid) => state.effectiveIds.has(sid)).length : undefined}
				formControlSx={formControlSx}
				selectSx={selectSx}
				dataTestId={dataTestId}
				ariaLabel={ariaLabel}
				icon={triggerIcon}
				hideCaret={hideTriggerCaret}
			/>

			{useSheet ? (
				<InHouseMobileSheet
					open={open}
					onClose={() => closePanel()}
					title={label ?? fieldLabel ?? t("system.dropdown.select")}
					dataTestId={dataTestId ? `${dataTestId}-sheet` : undefined}
					fitContent
					contentSx={{ p: 0 }}
					zIndex={panelZIndex}
				>
					<Box {...portalOverlayProps("dropdown-sheet")} onKeyDown={keyboard.onKeyDown}>
						{panel}
					</Box>
				</InHouseMobileSheet>
			) : (
				<Popper
					open={open}
					anchorEl={triggerRef.current}
					placement={placement}
					// Flip is disabled: we own the side via `placement` (from viewport geometry), so the panel can't
					// jump across the trigger when search shrinks the list. This also drops Popper's start↔end variation
					// flip, so near a viewport edge preventOverflow (still on) shifts the panel horizontally to stay
					// on-screen instead of re-anchoring to the trigger's other corner.
					popperOptions={{ strategy: "fixed", modifiers: [{ name: "flip", enabled: false }] }}
					sx={{ zIndex: panelZIndex }}
				>
					{({ placement }: { placement: PopperPlacementType }) => {
						// The Popper flips above the trigger near the viewport bottom; grow FROM the anchored edge
						// (origin + slide direction) so the panel never appears to expand away from the trigger.
						const flipped = placement.startsWith("top");
						return (
							<ClickAwayListener onClickAway={handleClickAway} mouseEvent="onPointerDown">
								{/* ClickAwayListener attaches its ref to this DOM Box; FocusTrap wraps the inner focusable div. */}
								<Box
									// Portaled to <body> by Popper: mark it so an enclosing popup's outside-click handler treats a
									// click in here as inside itself (see portalOverlay).
									{...portalOverlayProps("dropdown-panel")}
									data-testid={dataTestId ? `${dataTestId}-panel` : undefined}
									sx={{
										...(flipped ? { mb: 0.5 } : { mt: 0.5 }),
										backgroundColor: SURFACE,
										border: `1px solid ${NEUTRAL[200]}`,
										borderRadius: 3,
										boxShadow: SHADOW.FLOATING_WINDOW,
										overflow: "hidden",
										// The design system's menu: a rounder surface, its own drop, an inset and a 6px gap off the trigger.
										...(isMenuLook
											? {
													...(flipped ? { mb: `${FLOATING_MENU.GAP_PX}px` } : { mt: `${FLOATING_MENU.GAP_PX}px` }),
													borderRadius: `${RADIUS.POPOVER}px`,
													boxShadow: SHADOW.MENU_DROP,
													p: `${FLOATING_MENU.PADDING_PX}px`,
												}
											: {}),
										transformOrigin: flipped ? "bottom" : "top",
										"@keyframes dropdownPopDown": { from: { opacity: 0, transform: "translateY(-6px) scale(0.985)" }, to: { opacity: 1, transform: "none" } },
										"@keyframes dropdownPopUp": { from: { opacity: 0, transform: "translateY(6px) scale(0.985)" }, to: { opacity: 1, transform: "none" } },
										animation: `${flipped ? "dropdownPopUp" : "dropdownPopDown"} ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
										"@media (prefers-reduced-motion: reduce)": { animation: "none" },
									}}
								>
									{/* No disableRestoreFocus: closing must hand focus back to the trigger deterministically —
									left disabled, focus fell wherever the browser's post-unmount default landed it (often
									outside the ancestor's React subtree), which silently ate the next Escape/Tab press. */}
									<FocusTrap open={open} disableEnforceFocus>
										<Box tabIndex={-1} onKeyDown={keyboard.onKeyDown} sx={{ outline: "none" }}>
											{panel}
										</Box>
									</FocusTrap>
								</Box>
							</ClickAwayListener>
						);
					}}
				</Popper>
			)}
		</>
	);
};
